import React, { useCallback, useEffect, useState } from "react";
import { Alert, Card, Container, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import HotelAvailabilityAdmin from "./HotelAvailabilityAdmin";
import HotelOwnerBookings from "./HotelOwnerBookings";
import HotelOwnerRoomHolds from "./HotelOwnerRoomHolds";
import {
  fetchOwnerHotelAvailability,
  loginHotelOwner,
  updateOwnerHotelAvailability,
} from "../services/hotelOwnerAccessService";

export default function HotelOwnerPortal() {
  const { accessKey } = useParams();
  const storageKey = `hotelOwnerToken:${accessKey}`;
  const nameStorageKey = `hotelOwnerName:${accessKey}`;
  const roleStorageKey = `hotelOwnerRole:${accessKey}`;
  const [token, setToken] = useState(() => {
    // Sessions opened before the owner/manager split carry no role. Dropping them forces the
    // silent re-login below, so the page can never label itself as the wrong portal.
    const stored = sessionStorage.getItem(storageKey) || "";
    return stored && sessionStorage.getItem(roleStorageKey) ? stored : "";
  });
  const [displayName, setDisplayName] = useState(() => sessionStorage.getItem(nameStorageKey) || "");
  const [role, setRole] = useState(() => sessionStorage.getItem(roleStorageKey) || "");
  const isManager = role === "MANAGER";
  const portalLabel = role ? (isManager ? "Manager Portal" : "Owner Portal") : "";
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(() => !token);

  useEffect(() => {
    if (token) return;
    let alive = true;
    setLoading(true);
    setError("");
    loginHotelOwner(accessKey)
      .then((result) => {
        if (!alive) return;
        sessionStorage.setItem(storageKey, result.token);
        sessionStorage.setItem(nameStorageKey, result.displayName || "");
        sessionStorage.setItem(roleStorageKey, result.role || "");
        setDisplayName(result.displayName);
        setRole(result.role || "");
        setToken(result.token);
      })
      .catch((requestError) => {
        if (!alive) return;
        setError(requestError?.response?.data?.message || "Hotel portal link is invalid or inactive");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [accessKey, nameStorageKey, roleStorageKey, storageKey, token]);

  useEffect(() => {
    if (!portalLabel) return;
    document.title = displayName ? `${portalLabel} - ${displayName}` : portalLabel;
  }, [portalLabel, displayName]);

  const resetSession = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(nameStorageKey);
    sessionStorage.removeItem(roleStorageKey);
    setToken("");
    setDisplayName("");
    setRole("");
  }, [nameStorageKey, roleStorageKey, storageKey]);

  const fetchAvailability = useCallback(
    (startDate, endDate) => fetchOwnerHotelAvailability(accessKey, token, startDate, endDate),
    [accessKey, token],
  );
  const updateAvailability = useCallback(
    (payload) => updateOwnerHotelAvailability(accessKey, token, payload),
    [accessKey, token],
  );
  if (!token) {
    return (
      <Container className="py-5" style={{ maxWidth: "430px" }}>
        <Card className="shadow-sm">
          <Card.Body>
            <h4 className="mb-1">Hotel Availability</h4>
            <p className="text-muted">Opening your assigned hotel availability page.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            {loading && <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /> Loading...</div>}
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <div className="hotel-owner-portal">
      {/* Bookings first: the daily question a hotel opens this link to answer is who is arriving. */}
      <HotelOwnerBookings
        portalKey={accessKey}
        token={token}
        hotelName={displayName}
        showPayments={!isManager}
        portalLabel={portalLabel}
        onUnauthorized={resetSession}
      />
      <HotelOwnerRoomHolds portalKey={accessKey} token={token} onUnauthorized={resetSession} />
      <HotelAvailabilityAdmin
        title="Room Availability"
        subtitle={portalLabel
          ? `${portalLabel} - manage availability for your assigned hotels`
          : "Manage availability for your assigned hotels"}
        showOwnerAccess={false}
        showSoldOutActions={false}
        showCopySummary
        directManagementView
        fetchAvailability={fetchAvailability}
        updateAvailability={updateAvailability}
        onUnauthorized={resetSession}
      />
    </div>
  );
}
